/**
 * Menta-Pack Cart Functionality
 * Maneja toda la funcionalidad relacionada con el carrito:
 * - Actualización de cantidad de productos
 * - Actualización de precios
 * - Actualización de totales en todos los elementos
 * - Sincronización entre carrito principal y carrito lateral
 */

if (typeof CartForm !== "function") {
  class CartForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector("form");
      this.init();
    }

    /**
     * Inicializa todos los componentes del carrito
     */
    init() {
      this.ajaxifyCartItems();
      this.setupQuantityListeners();
      this.setupCartEventListeners();
    }

    /**
     * Configura todos los event listeners del carrito
     */
    setupCartEventListeners() {
      // Escuchar eventos estándar de Shopify
      document.addEventListener('product:added', this.handleCartUpdate.bind(this));
      document.addEventListener('cart:refresh', this.handleCartUpdate.bind(this));
      document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
      
      // Interceptar peticiones XHR para detectar actualizaciones del carrito
      this.setupXHRInterceptor();
      
      // Escuchar formularios de adición al carrito
      document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
        form.addEventListener('submit', () => {
          setTimeout(this.handleCartUpdate.bind(this), 800);
        });
      });
    }
    
    /**
     * Configura un interceptor para peticiones XHR relacionadas con el carrito
     */
    setupXHRInterceptor() {
      const originalXHROpen = window.XMLHttpRequest.prototype.open;
      const originalXHRSend = window.XMLHttpRequest.prototype.send;
      const self = this;
      
      // Guardar la URL original de la petición
      window.XMLHttpRequest.prototype.open = function() {
        this._url = arguments[1];
        return originalXHROpen.apply(this, arguments);
      };
      
      // Interceptar peticiones relacionadas con el carrito
      window.XMLHttpRequest.prototype.send = function() {
        const url = this._url;
        if (url && (url.includes('/cart/add') || url.includes('/cart/update') || url.includes('/cart/change'))) {
          this.addEventListener('load', function() {
            setTimeout(self.handleCartUpdate.bind(self), 500);
          });
        }
        return originalXHRSend.apply(this, arguments);
      };
    }
    
    /**
     * Maneja cualquier evento de actualización del carrito
     */
    handleCartUpdate() {
      this.refreshCart();
    }

    /**
     * Obtiene datos actualizados del carrito y actualiza todos los elementos relacionados
     */
    refreshCart() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cartData => {
          this.updateAllTotals(cartData);
          this.updateHeaderCartButtons(cartData);
          this.updateSidebarCart(cartData);
          
          // Notificar a otros componentes
          document.dispatchEvent(new CustomEvent('cart:refreshed', { 
            detail: { cart: cartData } 
          }));
        })
        .catch(error => {
          console.error('Error al actualizar carrito:', error);
        });
    }

    /**
     * Configura listeners para cambios de cantidad en productos del carrito
     */
    setupQuantityListeners() {
      this.addEventListener('quantity-updated', (e) => {
        if (e.detail && e.detail.quantity && e.detail.line) {
          const cartItem = this.querySelector(`[data-js-cart-item][data-line="${e.detail.line}"]`);
          if (cartItem) {
            this.updateCartQty(cartItem, e.detail.quantity);
          }
        }
      });
    }

    /**
     * Prepara los elementos del carrito para actualizaciones AJAX
     */
    ajaxifyCartItems() {
      if (!this.form) return;
      
      this.querySelectorAll("[data-js-cart-item]").forEach((item) => {
        const remove = item.querySelector(".remove");
        if (remove) {
          remove.dataset.href = remove.getAttribute("href");
          remove.setAttribute("href", "");
          remove.addEventListener("click", (e) => {
            e.preventDefault();
            this.updateCartQty(item, 0);
          });
        }
      });
    }
    
    /**
     * Actualiza la cantidad de un producto en el carrito
     * @param {HTMLElement} item - El elemento del carrito a actualizar
     * @param {number} newQty - La nueva cantidad
     */
    updateCartQty(item, newQty) {
      if (!this.form) return;
      
      let alert = null;
      this.form.classList.add("processing");
      
      if (this.querySelector(".alert")) {
        this.querySelector(".alert").remove();
      }
      
      const body = JSON.stringify({
        line: parseInt(item.dataset.line),
        quantity: newQty,
      });
      
      fetch(KROWN.settings.routes.cart_change_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/javascript",
        },
        body,
      })
        .then(response => response.json())
        .then(cartData => {
          if (cartData.status == 422) {
            alert = document.createElement("span");
            alert.classList.add("alert", "alert--error");
            alert.innerHTML = cartData.description || cartData.message;
          }
          
          return fetch("?section_id=helper-cart")
            .then(response => response.text())
            .then(text => {
              // Actualizar el formulario del carrito
              this.updateCartDOM(text, cartData, alert);
            });
        })
        .catch(error => {
          console.error('Error:', error);
          const alert = document.createElement("span");
          alert.classList.add("alert", "alert--error");
          alert.textContent = KROWN.settings.locales.cart_general_error;
          this.form.prepend(alert);
        })
        .finally(() => {
          this.form.classList.remove("processing");
        });
    }
    
    /**
     * Actualiza el DOM del carrito con los nuevos datos
     * @param {string} html - HTML del carrito actualizado
     * @param {Object} cartData - Datos del carrito
     * @param {HTMLElement} alert - Elemento de alerta (opcional)
     */
    updateCartDOM(html, cartData, alert) {
      const sectionInnerHTML = new DOMParser().parseFromString(html, "text/html");
      
      // Actualizar elementos del carrito
      const cartItems = document.getElementById("AjaxCartForm");
      const newItems = sectionInnerHTML.getElementById("AjaxCartForm");
      if (cartItems && newItems) {
        cartItems.innerHTML = newItems.innerHTML;
      }
      
      // Actualizar contadores del carrito
      const cartCount = cartData.item_count || 0;
      document.querySelectorAll("[data-header-cart-count], [data-cart-count], .cart__count, .cart_count").forEach(elm => {
        elm.textContent = cartCount;
      });
      
      // Actualizar todos los elementos relacionados con el carrito
      this.updateAllTotals(cartData);
      this.updateHeaderCartButtons(cartData);
      this.updateSidebarCart(cartData);
      
      // Mostrar alerta si es necesario
      if (alert) {
        this.form.prepend(alert);
      }
      
      // Notificar actualización
      this.dispatchEvent(new Event("cart-updated"));
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart: cartData } 
      }));
    }
    
    /**
     * Actualiza todos los totales del carrito en la página
     * @param {Object} cartData - Datos del carrito
     */
    updateAllTotals(cartData) {
      if (!cartData) return;
      
      const totalPrice = cartData.total_price || 0;
      const formattedTotal = this.formatMoney(totalPrice);
      
      // Actualizar elementos con el total
      document.querySelectorAll('.cart__total, .cart-total, .cart_total, .total, [data-cart-total]').forEach(el => {
        const totalElement = el.querySelector('strong, span, .total-price, .text-size--heading') || el;
        if (!el.getAttribute('aria-hidden')) {
          totalElement.textContent = formattedTotal;
        }
      });
      
      // Actualizar específicamente elementos con heading
      document.querySelectorAll('.text-size--heading, .cart__total strong, .cart_total strong').forEach(el => {
        el.textContent = formattedTotal;
      });
    }
    
    /**
     * Actualiza los botones del carrito en la cabecera
     * @param {Object} cartData - Datos del carrito
     */
    updateHeaderCartButtons(cartData) {
      if (!cartData) return;
      
      const total = cartData.total_price || 0;
      const itemCount = cartData.item_count || 0;
      const formattedTotal = this.formatMoney(total);
      const headerCartText = `${formattedTotal} (${itemCount})`;
      
      // Actualizar botones del carrito
      const cartButtonSelectors = [
        '[data-js-sidebar-handle][title*="carrito"]',
        '[aria-controls="site-cart-sidebar"]',
        '.button--solid[title*="carrito"]',
        '.mobile-cart-button',
        '[href="#/cart/"]',
        'a.button[title*="carrito"]',
        '[class*="cart"][title*="carrito"]'
      ].join(', ');
      
      document.querySelectorAll(cartButtonSelectors).forEach(button => {
        if (button.getAttribute('title')?.toLowerCase().includes('carrito') || 
            button.classList.contains('button--solid')) {
          button.textContent = headerCartText;
        }
      });
    }
    
    /**
     * Actualiza el carrito lateral
     * @param {Object} cartData - Datos del carrito
     */
    updateSidebarCart(cartData) {
      if (!cartData) return;
      
      const total = cartData.total_price || 0;
      const formattedTotal = this.formatMoney(total);
      
      // Actualizar elementos en el carrito lateral
      const sidebarSelectors = '#CartDrawer, #site-cart-sidebar, .sidebar-drawer, .cart-drawer, .sidebar__cart';
      document.querySelectorAll(sidebarSelectors).forEach(container => {
        // Actualizar headings
        container.querySelectorAll('.text-size--heading').forEach(el => {
          el.textContent = formattedTotal;
        });
        
        // Actualizar totales
        container.querySelectorAll('[data-cart-total]').forEach(el => {
          el.textContent = formattedTotal;
        });
        
        // Actualizar otros elementos de total
        container.querySelectorAll('.cart__total, .cart_total').forEach(el => {
          const strong = el.querySelector('strong');
          if (strong) {
            strong.textContent = formattedTotal;
          } else {
            el.textContent = formattedTotal;
          }
        });
      });
      
      // Actualizar específicamente AjaxCartSubtotal
      const ajaxCartSubtotal = document.getElementById('AjaxCartSubtotal');
      if (ajaxCartSubtotal) {
        ajaxCartSubtotal.querySelectorAll('.text-size--heading, strong').forEach(el => {
          el.textContent = formattedTotal;
        });
      }
    }
    
    /**
     * Verifica si un elemento es visible
     * @param {HTMLElement} element - Elemento a verificar
     * @returns {boolean} - true si es visible, false si no
     */
    isElementVisible(element) {
      if (!element) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }
    
    /**
     * Formatea un valor en centavos a formato de moneda
     * @param {number} cents - Precio en centavos
     * @returns {string} - Precio formateado
     */
    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      return `€${(cents/100).toFixed(2).replace('.', ',')}`;
    }
  }
  
  if (typeof customElements.get("cart-form") == "undefined") {
    customElements.define("cart-form", CartForm);
  }
}

/**
 * Componente para recomendaciones en el carrito
 */
if (typeof customElements.get("cart-recommendations") == "undefined") {
  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.generateRecommendations();
    }
    
    generateRecommendations() {
      const cartItems = document.getElementById('AjaxCartForm')?.querySelectorAll('[data-js-cart-item]') || [];
      if (cartItems.length === 0) return;
        
      fetch(`${KROWN.settings.routes.product_recommendations_url}?section_id=${this.dataset.section}&product_id=${cartItems[0].dataset.productId}&limit=${this.dataset.limit}`)
        .then(response => response.text())
        .then(text => {
          const innerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('[data-js-cart-recommendations-performed]');
            
          if (innerHTML && innerHTML.querySelectorAll('[data-js-product-item]').length > 0) {
            this.innerHTML = innerHTML.innerHTML;
          }
        })
        .catch(error => {
          console.error('Error cargando recomendaciones:', error);
        });
    }
  }
  
  customElements.define('cart-recommendations', CartRecommendations);
}

/**
 * Manejador global para páginas sin cart-form
 */
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('cart-form')) return;
  
  // Interceptar botones de añadir al carrito
  const addToCartButtons = document.querySelectorAll('[data-js-product-add-to-cart], form[action="/cart/add"] button[type="submit"]');
  addToCartButtons.forEach(button => {
    button.addEventListener('click', () => {
      setTimeout(updateGlobalCart, 800);
    });
  });
  
  // Interceptar peticiones XHR para el carrito
  const originalXHROpen = window.XMLHttpRequest.prototype.open;
  const originalXHRSend = window.XMLHttpRequest.prototype.send;
  
  window.XMLHttpRequest.prototype.open = function() {
    this._url = arguments[1];
    return originalXHROpen.apply(this, arguments);
  };
  
  window.XMLHttpRequest.prototype.send = function() {
    const url = this._url;
    if (url && (url.includes('/cart/add') || url.includes('/cart/update') || url.includes('/cart/change'))) {
      this.addEventListener('load', () => {
        setTimeout(updateGlobalCart, 500);
      });
    }
    return originalXHRSend.apply(this, arguments);
  };
  
  // Escuchar eventos estándar
  document.addEventListener('product:added', () => {
    setTimeout(updateGlobalCart, 500);
  });
  
  /**
   * Función para actualizar el carrito globalmente
   */
  function updateGlobalCart() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cartData => {
        const total = cartData.total_price || 0;
        const itemCount = cartData.item_count || 0;
        const formattedTotal = `€${(total/100).toFixed(2).replace('.', ',')}`;
        const headerCartText = `${formattedTotal} (${itemCount})`;
        
        // Actualizar botones del carrito
        const cartButtonSelectors = [
          '[data-js-sidebar-handle][title*="carrito"]',
          '[aria-controls="site-cart-sidebar"]',
          '.button--solid[title*="carrito"]',
          '.mobile-cart-button',
          '[href="#/cart/"]',
          'a.button[title*="carrito"]',
          '[class*="cart"][title*="carrito"]'
        ].join(', ');
        
        document.querySelectorAll(cartButtonSelectors).forEach(button => {
          if (button.getAttribute('title')?.toLowerCase().includes('carrito') || 
              button.classList.contains('button--solid')) {
            button.textContent = headerCartText;
          }
        });
        
        // Actualizar contadores
        document.querySelectorAll('[data-header-cart-count], [data-cart-count], .cart__count, .cart_count').forEach(elm => {
          elm.textContent = itemCount;
        });
        
        // Notificar actualización
        document.dispatchEvent(new CustomEvent('cart:updated', { 
          detail: { cart: cartData } 
        }));
      })
      .catch(error => {
        console.error('Error al actualizar carrito (global):', error);
      });
  }
  
  // Primera actualización
  updateGlobalCart();
});